import { z } from "zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import InputField from "@/components/form/components/InputField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  useCreateWorkLogMutation,
  useUpdateWorkLogMutation,
} from "@/hooks/api/workLog.queries";
import { WorkLogType } from "@/services/workLog.service";
import { useEffect } from "react";
import { Clock, Save, AlertCircle } from "lucide-react";

const workLogSchema = z.object({
  log_date: z.string().min(1, "Date is required"),
  hours_worked: z.coerce.number().min(0.5, "Minimum 0.5 hours required").max(24, "Maximum 24 hours allowed"),
  tasks_completed: z.string().min(10, "Please describe tasks in at least 10 characters"),
  challenges: z.string().optional(),
});

type WorkLogFormValues = z.infer<typeof workLogSchema>;

interface WorkLogDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  initialData?: WorkLogType | null;
  applicationId: string; // Required for creation
}

const WorkLogDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
  initialData,
  applicationId,
}: WorkLogDialogProps) => {
  const isEditing = !!initialData;
  const createMutation = useCreateWorkLogMutation();
  const updateMutation = useUpdateWorkLogMutation(initialData?.id || "");

  const form = useForm<WorkLogFormValues>({
    resolver: zodResolver(workLogSchema) as any,
    defaultValues: {
      log_date: new Date().toISOString().split("T")[0],
      hours_worked: 8,
      tasks_completed: "",
      challenges: "",
    },
  });

  useEffect(() => {
    if (initialData && isOpen) {
      form.reset({
        log_date: initialData.log_date,
        hours_worked: initialData.hours_worked,
        tasks_completed: initialData.tasks_completed,
        challenges: initialData.challenges || "",
      });
    } else if (!isOpen) {
      form.reset({
        log_date: new Date().toISOString().split("T")[0],
        hours_worked: 8,
        tasks_completed: "",
        challenges: "",
      });
    }
  }, [initialData, isOpen, form]);

  const onSubmit: SubmitHandler<WorkLogFormValues> = (values) => {
    if (isEditing && initialData) {
      updateMutation.mutate(values, {
        onSuccess: () => {
          onCancel();
        },
      });
    } else {
      createMutation.mutate(
        { ...values, application_id: applicationId },
        {
          onSuccess: () => {
            onCancel();
          },
        }
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] rounded-[2.5rem] border-none shadow-2xl p-0 overflow-hidden bg-white">
        <DialogHeader className="bg-indigo-600 p-10 text-white relative">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="relative flex items-center gap-4">
             <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md border border-white/10">
                <Clock size={28} className="text-white" />
             </div>
             <div>
                <DialogTitle className="text-3xl font-black tracking-tight">{isEditing ? "Edit Work Log" : "Log Daily Work"}</DialogTitle>
                <DialogDescription className="text-indigo-100 font-medium opacity-80">
                  {isEditing ? "Update your hours and tasks for this day." : "Record your progress and internship hours."}
                </DialogDescription>
             </div>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-10 space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <InputField
                type="date"
                name="log_date"
                label="Log Date"
                className={{
                  labelClass: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400",
                  inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600 font-bold"
                }}
              />

              <InputField
                type="number"
                step="0.5"
                name="hours_worked"
                label="Hours Involved"
                className={{
                  labelClass: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400",
                  inputClass: "h-12 rounded-xl bg-slate-50 border-none focus-visible:ring-indigo-600 font-bold"
                }}
              />
            </div>

            <TextareaField
              name="tasks_completed"
              label="Tasks Completed"
              placeholder="What did you accomplish today?"
              className={{
                labelClass: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400",
                textareaClass: "min-h-[120px] rounded-2xl bg-slate-50 border-none focus-visible:ring-indigo-600 p-4 font-medium resize-none"
              }}
            />

            <TextareaField
              name="challenges"
              label="Challenges / Blockers (Optional)"
              placeholder="Any difficulties faced?"
              className={{
                labelClass: "text-[10px] font-black uppercase tracking-[0.2em] text-slate-400",
                textareaClass: "min-h-[80px] rounded-2xl bg-slate-50 border-none focus-visible:ring-indigo-600 p-4 font-medium resize-none shadow-inner"
              }}
            />

            {createMutation.isError && (
              <div className="flex items-center gap-3 p-4 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold border border-rose-100">
                <AlertCircle size={16} />
                <span>Failed to save log. Please ensure you have an active internship.</span>
              </div>
            )}

            <DialogFooter className="pt-4 flex gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                className="flex-1 h-14 rounded-2xl text-slate-500 font-black hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 font-black shadow-xl shadow-indigo-100 text-white transition-all active:scale-95"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                   "Processing..."
                ) : (
                  <span className="flex items-center gap-2">
                    <Save size={20} />
                    {isEditing ? "Update Log" : "Save Daily Log"}
                  </span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkLogDialog;
